import { LitElement, html, css, nothing, svg, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { palette } from "./theme";
import type { Option } from "./protocol";
import {
  battlefieldPreview, cardBackPreview, cardBackUrl, cardFramePreview, cardFrameStyles, cardBackStyles, namePlatePreview,
  namePlateStyles, previewStyles, type Battlefield, type CardBack, type Style,
} from "./cosmetics";

// A player's level and what it unlocks, as GET me/progress sends it (platform/progression).

export type Cosmetic = "card-back" | "card-frame" | "name-plate" | "battlefield";

type ProgressJson = {
  level: number;
  xp: number;
  level_xp: number;
  next_level_xp: number;
  card_back: Option<string>;
  card_frame: Option<string>;
  name_plate: Option<string>;
  battlefield: Option<string>;
  unlocks: { cosmetic: Cosmetic; level: number }[];
  level_starts: number[];
  card_frames: Style[];
  name_plates: Style[];
  battlefields: Battlefield[];
};

export type Progress = {
  level: number;
  xp: number;
  // The XP this level began at, and the XP the next one does.
  level_xp: number;
  next_level_xp: number;
  card_back: string | null;
  card_frame: string | null;
  name_plate: string | null;
  battlefield: string | null;
  unlocks: { cosmetic: Cosmetic; level: number }[];
  // The XP each level begins at, from level 1 to the one after the last unlock or the player's own.
  level_starts: number[];
  // What there is to choose from, but the card backs, which Settings fetches when it shows them.
  card_frames: Style[];
  name_plates: Style[];
  battlefields: Battlefield[];
};

export function progressOf(json: ProgressJson): Progress {
  const some = (o: Option<string>) => (o.kind === "Some" ? o.values[0] : null);
  return {
    ...json,
    card_back: some(json.card_back), card_frame: some(json.card_frame),
    name_plate: some(json.name_plate), battlefield: some(json.battlefield),
  };
}

export function levelUnlocking(p: Progress, cosmetic: Cosmetic): number {
  return p.unlocks.find((u) => u.cosmetic === cosmetic)?.level ?? Infinity;
}

function xpToReach(p: Progress, level: number): number {
  return Math.max(0, (p.level_starts[level - 1] ?? Infinity) - p.xp);
}

const COSMETIC_TITLE: Record<Cosmetic, string> = {
  "card-back": "Card back", "card-frame": "Card frame", "name-plate": "Name plate", battlefield: "Battlefield",
};

function chosenOf(p: Progress, cosmetic: Cosmetic): string | null {
  switch (cosmetic) {
    case "card-back": return p.card_back;
    case "card-frame": return p.card_frame;
    case "name-plate": return p.name_plate;
    case "battlefield": return p.battlefield;
  }
}

// What the player chose, by its name where the progress carries one.
function chosenName(p: Progress, cosmetic: Cosmetic, id: string): string {
  switch (cosmetic) {
    case "card-back": return "Yours";
    case "card-frame": return p.card_frames.find((s) => s.id === id)?.name ?? id;
    case "name-plate": return p.name_plates.find((s) => s.id === id)?.name ?? id;
    case "battlefield": return p.battlefields.find((b) => b.id === id)?.card ?? id;
  }
}

function previewOf(cosmetic: Cosmetic, id: string, label: string): TemplateResult {
  switch (cosmetic) {
    case "card-back": return cardBackPreview(id);
    case "card-frame": return cardFramePreview(id);
    case "name-plate": return namePlatePreview(id, label);
    case "battlefield": return battlefieldPreview(id);
  }
}

const xp = (n: number) => n.toLocaleString("en");

// The level, on a planeswalker's loyalty shield.
export function levelShield(level: number, locked = false): TemplateResult {
  return html`<span class="shield ${locked ? "locked" : ""}" aria-hidden="true">
    <svg viewBox="0 0 36 40">${svg`<path d="M18 1.5 33 6v15c0 9-6.5 14.5-15 17.5C9.5 35.5 3 30 3 21V6Z"></path>`}</svg><b>${level}</b>
  </span>`;
}

// The banner's level, beside the player's name.
export function renderLevel(p: Progress): TemplateResult {
  return html`<div class="level">${levelShield(p.level)}<span><b>Level ${p.level}</b><small>${xp(p.xp)} XP</small></span></div>`;
}

function unlockStatus(p: Progress, cosmetic: Cosmetic, level: number, settingsHref: string): TemplateResult {
  const chosen = chosenOf(p, cosmetic);
  if (p.level < level) return html`Level ${level}, ${xp(xpToReach(p, level))} XP to go`;
  if (chosen) return html`${chosenName(p, cosmetic, chosen)}. <a href=${settingsHref}>Change it</a>`;
  return html`Unlocked at level ${level}`;
}

// The climb to each unlock: a step per level, all as wide, each as tall as the XP it asks, filled as
// far as the player has climbed, with what each unlock gives hanging from the step that reaches it.
export function renderLevelRoad(p: Progress, settingsHref: string): TemplateResult {
  const starts = p.level_starts;
  const costs = starts.slice(1).map((start, i) => start - starts[i]);
  const steepest = Math.max(...costs);
  const through = Math.min(1, (p.xp - p.level_xp) / Math.max(1, p.next_level_xp - p.level_xp));
  const at = (levelsClimbed: number) => `${Math.min(100, (100 * levelsClimbed) / costs.length)}%`;
  return html`
    <div class="road" style=${`--here: ${at(p.level - 1 + through)}`}>
      <span class="road-here">${xp(p.xp)} XP</span>
      <div class="steps" style=${`grid-template-columns: repeat(${costs.length}, 1fr)`}>
        ${costs.map((cost, i) => {
          const fill = Math.min(1, Math.max(0, (p.xp - starts[i]) / cost));
          return html`<span class="step ${fill === 1 ? "climbed" : ""}" title="Level ${i + 1}, from ${xp(starts[i])} XP"
            style=${`--rise: ${cost / steepest}; --fill: ${fill * 100}%`}></span>`;
        })}
      </div>
      <div class="unlocks">
        ${p.unlocks.map((u, i) => {
          const chosen = chosenOf(p, u.cosmetic);
          const locked = p.level < u.level;
          const last = i === p.unlocks.length - 1;
          return html`<div class="unlock ${locked ? "locked" : ""} ${last ? "from-right" : ""}" style=${`--at: ${at(u.level - 1)}`}>
            ${!locked && !chosen
              ? html`<a class="unlock-art open" href=${settingsHref}>Choose one</a>`
              : html`<span class="unlock-art ${chosen ? "" : "unchosen"}">
                  ${locked ? levelShield(u.level, true) : previewOf(u.cosmetic, chosen!, chosenName(p, u.cosmetic, chosen!))}
                </span>`}
            <span class="unlock-text"><b>${COSMETIC_TITLE[u.cosmetic]}</b>
              <span>${unlockStatus(p, u.cosmetic, u.level, settingsHref)}</span></span>
          </div>`;
        })}
      </div>
    </div>
  `;
}

// What the game just finished earned, in its result banner: the XP, the bar filling to it, and any
// level reached and what that unlocked.
export function renderXpEarned(before: Progress, after: Progress): TemplateResult | typeof nothing {
  const earned = after.xp - before.xp;
  if (earned <= 0) return nothing;
  const levelled = after.level > before.level;
  const through = (p: Progress) => (100 * (p.xp - p.level_xp)) / (p.next_level_xp - p.level_xp);
  const unlocked = after.unlocks.filter((u) => u.level > before.level && u.level <= after.level);
  return html`
    <div class="earned ${levelled ? "levelled" : ""}" role="status">
      ${levelShield(after.level)}
      <div class="earned-text">
        <span><b>+${xp(earned)} XP</b> ${levelled ? `You reached level ${after.level}` : `${xp(after.next_level_xp - after.xp)} XP to level ${after.level + 1}`}</span>
        <span class="earned-bar" style=${`--from: ${levelled ? 0 : through(before)}%; --to: ${through(after)}%`}><span></span></span>
        ${unlocked.map((u) => html`<span class="earned-unlock">${COSMETIC_TITLE[u.cosmetic]}s are unlocked: choose one in Settings.</span>`)}
      </div>
    </div>
  `;
}

const shieldStyles = css`
  .shield { display: inline-grid; place-items: center; flex: none; width: var(--shield, 36px); aspect-ratio: 36 / 40; }
  .shield > * { grid-area: 1 / 1; }
  .shield svg { width: 100%; height: 100%; overflow: visible; }
  .shield path { fill: #161616; stroke: var(--lavender); stroke-width: 1.75; stroke-linejoin: round; }
  .shield b { margin-top: -6%; font-size: calc(var(--shield, 36px) * 0.44); font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; color: var(--text-strong); }
  .shield.locked path { fill: rgb(0 0 0 / 0.35); stroke: var(--text-faint); stroke-dasharray: 3 2.5; }
  .shield.locked b { color: var(--text-dim); }
`;

export const progressStyles = [shieldStyles, previewStyles, cardBackStyles, cardFrameStyles, namePlateStyles, css`
  .level { display: flex; align-items: center; gap: 10px; margin-top: 14px; --shield: 40px; }
  .level > span { display: grid; line-height: 1.25; }
  .level b { font-size: 15px; font-weight: 600; color: var(--text-strong); }
  .level small { font-size: 13px; color: rgb(255 255 255 / 0.7); font-variant-numeric: tabular-nums; }

  .road { position: relative; margin: 6px 0 8px; padding-top: 26px; container-type: inline-size; }
  .road-here {
    position: absolute; top: 0; left: clamp(36px, var(--here), calc(100% - 36px)); transform: translateX(-50%);
    font-size: 12px; font-weight: 600; white-space: nowrap; font-variant-numeric: tabular-nums; color: var(--lavender);
  }
  .road::before { content: ""; position: absolute; top: 18px; bottom: calc(100% - 26px - 40px); left: var(--here); width: 2px; margin-left: -1px; background: var(--lavender); }
  .steps { display: grid; align-items: end; gap: 2px; height: 40px; }
  .step {
    height: calc(8px + var(--rise) * 32px); border-radius: 2px 2px 0 0;
    background: linear-gradient(90deg, var(--lavender) var(--fill), var(--surface-3) var(--fill));
  }
  .step.climbed { background: var(--brand-hi); }
  .unlocks { position: relative; height: 172px; border-top: 1px solid var(--border-strong); }
  .unlock { position: absolute; top: 0; left: var(--at); width: 150px; display: grid; gap: 8px; padding: 14px 0 0 12px; border-left: 1px solid var(--lavender); }
  .unlock.from-right { left: auto; right: calc(100% - var(--at)); padding: 14px 12px 0 0; border-left: 0; border-right: 1px solid var(--lavender); text-align: right; }
  .unlock.locked { border-color: var(--border-strong); }
  .unlock-art {
    display: grid; place-items: center; aspect-ratio: 16 / 10; border-radius: 5px; overflow: hidden; --shield: 34px;
    background: var(--surface-3); box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.08);
  }
  .unlock-art > .field-preview, .unlock-art > .plate-preview { width: 100%; height: 100%; box-sizing: border-box; border-radius: 0; }
  .unlock-art > .back-preview, .unlock-art > .frame-preview { height: 84%; --card-w: 60px; }
  .unlock-art.unchosen { background: repeating-linear-gradient(135deg, #262626 0 6px, #2b2b2b 6px 12px); }
  .unlock-art.open { background: none; border: 1px dashed var(--lavender); color: var(--lavender); font-size: 14px; font-weight: 600; text-decoration: none; }
  .unlock-art.open:hover, .unlock-art.open:focus-visible { background: var(--lavender-soft); }
  .unlock-text { display: grid; gap: 2px; font-size: 13px; line-height: 1.35; color: var(--text-dim); }
  .unlock-text b { font-size: 14px; font-weight: 600; color: var(--text-strong); }
  .unlock.locked .unlock-text b { color: var(--text); }
  /* Under 1000px of road, the last two unlocks (levels 35 and 50) would overlap; they list instead. */
  @container (max-width: 1000px) {
    .unlocks { height: auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 16px; padding-top: 14px; }
    .unlock, .unlock.from-right { position: static; width: auto; padding: 0; border: 0; text-align: left; }
  }

  .earned { display: flex; align-items: center; gap: 14px; margin-top: 22px; --shield: 46px; font-size: 15px; font-weight: 400; line-height: 1.35; }
  .earned-text { display: grid; gap: 7px; min-width: 0; width: min(320px, 100%); color: rgb(255 255 255 / 0.85); }
  .earned-text b { font-size: 18px; font-weight: 700; color: var(--text-strong); margin-right: 6px; }
  .earned-bar { display: block; height: 6px; border-radius: 3px; background: rgb(0 0 0 / 0.4); overflow: hidden; }
  .earned-bar span { display: block; height: 100%; width: var(--to); background: var(--lavender); animation: earned-fill 1100ms var(--ease-out) 400ms both; }
  .earned-unlock { color: var(--text-strong); font-weight: 600; }
  .earned.levelled .shield { animation: levelled 700ms var(--ease-out) 1400ms both; }
  @keyframes earned-fill { from { width: var(--from); } to { width: var(--to); } }
  @keyframes levelled {
    0% { transform: scale(1); filter: none; }
    45% { transform: scale(1.22); filter: drop-shadow(0 0 10px var(--lavender)); }
    100% { transform: scale(1); filter: drop-shadow(0 0 4px rgb(201 153 230 / 0.6)); }
  }
  @media (prefers-reduced-motion: reduce) {
    .earned-bar span, .earned.levelled .shield { animation: none; }
  }
`];

// A choice of one look for a cosmetic: locked until the player's level unlocks it, then every look
// there is, the chosen one marked and clearable. The 483 card backs are searchable, and shown a page
// at a time.
@customElement("cosmetic-picker")
export class CosmeticPicker extends LitElement {
  @property() cosmetic: Cosmetic = "card-back";
  @property({ attribute: false }) progress: Progress | null = null;
  @property({ attribute: false }) findCardBacks: () => Promise<CardBack[]> = async () => [];
  // Saves the choice (null clears it); the progress the server sends back, or why it refused.
  @property({ attribute: false }) choose: (id: string | null) => Promise<Progress | string> = async () => "not wired";
  @property({ attribute: false }) chosen: (p: Progress) => void = () => {};

  @state() private cardBacks: CardBack[] | null = null;
  @state() private filter = "";
  @state() private shown = BACKS_PER_PAGE;
  @state() private error: string | null = null;
  @state() private saving = false;

  static styles = [palette, shieldStyles, previewStyles, cardBackStyles, cardFrameStyles, namePlateStyles, css`
    :host { display: block; max-width: 720px; margin-top: 10px; }
    .locked { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 6px; border: 1px dashed var(--border-strong); --shield: 34px; }
    .locked span { display: grid; gap: 1px; font-size: 13px; color: var(--text-dim); }
    .locked b { font-size: 14px; font-weight: 600; color: var(--text); }
    .bar { display: flex; align-items: center; gap: 14px; margin-bottom: 10px; font-size: 13px; color: var(--text-dim); }
    input { font: inherit; padding: 6px 10px; border-radius: 3.5px; border: 1px solid #000; background: var(--input); color: #bbb; flex: 1; min-width: 0; }
    .looks { display: grid; gap: 12px 10px; grid-template-columns: repeat(auto-fill, minmax(var(--tile, 96px), 1fr)); }
    .looks.backs { --tile: 92px; }
    .looks.frames { --tile: 84px; }
    .looks.plates { --tile: 132px; }
    .looks.fields { --tile: 200px; }
    .look {
      display: grid; align-content: start; gap: 5px; padding: 0; border: 0; background: none; min-width: 0;
      cursor: var(--cursor-pointer, pointer); color: var(--text-dim); font: inherit; font-size: 12px; line-height: 1.3; text-align: left;
    }
    .look > :first-child { outline: 2px solid transparent; outline-offset: 2px; border-radius: 5px; }
    .look:hover > :first-child, .look:focus-visible > :first-child { outline-color: var(--border-strong); }
    .look[aria-pressed="true"] > :first-child { outline-color: var(--lavender); }
    .look:hover, .look:focus-visible { color: var(--text-strong); outline: none; }
    .look:disabled { opacity: 0.5; }
    .look b { font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .look small { font-size: 11px; color: var(--text-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .look[aria-pressed="true"] b { color: var(--lavender); }
    /* MPC Autofill's scans carry the print bleed, cropped away here as in the table's backs. */
    .back { display: block; position: relative; aspect-ratio: 63 / 88; overflow: hidden; border-radius: 4.5% / 3.2%; background: #141414; box-shadow: 0 1px 3px rgb(0 0 0 / 0.6); }
    .back img { position: absolute; left: -5%; top: -3.57%; width: 110%; height: 107.14%; object-fit: cover; }
    .more { margin-top: 12px; }
    .link-button { background: none; border: 0; padding: 0; font: inherit; font-size: 13px; color: var(--lavender); cursor: var(--cursor-pointer, pointer); }
    .link-button:hover { text-decoration: underline; }
    .error { color: var(--danger, #e06c6c); margin-top: 6px; }
  `];

  willUpdate(): void {
    const p = this.progress;
    if (this.cosmetic !== "card-back" || this.cardBacks !== null || !p || p.level < levelUnlocking(p, this.cosmetic)) return;
    this.cardBacks = [];
    this.findCardBacks().then(
      (backs) => { this.cardBacks = backs; },
      (err: unknown) => { this.cardBacks = null; this.error = `The card backs didn't load: ${err instanceof Error ? err.message : String(err)}`; },
    );
  }

  private async saveChoice(id: string | null): Promise<void> {
    this.saving = true;
    this.error = null;
    try {
      const saved = await this.choose(id);
      if (typeof saved === "string") this.error = saved;
      else this.chosen(saved);
    } finally {
      this.saving = false;
    }
  }

  private renderLook(id: string, current: string | null, label: string, preview: TemplateResult, credit: string | null = null): TemplateResult {
    const chosen = id === current;
    return html`<button class="look" aria-pressed=${chosen ? "true" : "false"} ?disabled=${this.saving}
      title=${chosen ? `${label}, chosen` : `Use ${label}`} @click=${() => { if (!chosen) void this.saveChoice(id); }}>
      ${preview}<b>${label}</b>${credit ? html`<small>${credit}</small>` : nothing}
    </button>`;
  }

  private renderCardBacks(current: string | null): TemplateResult {
    const backs = this.cardBacks ?? [];
    if (backs.length === 0) return html`<p class="bar">${this.error ? "" : "Fetching the card backs…"}</p>`;
    const words = this.filter.toLowerCase().split(/\s+/).filter(Boolean);
    const matching = backs.filter((b) => words.every((w) => `${b.name} ${b.creator}`.toLowerCase().includes(w)));
    const page = matching.slice(0, this.shown);
    return html`
      <div class="bar">
        <input type="search" placeholder="Search ${backs.length} card backs by name or maker" aria-label="Search the card backs"
          .value=${this.filter} @input=${(e: Event) => { this.filter = (e.target as HTMLInputElement).value; this.shown = BACKS_PER_PAGE; }}>
        <span>${matching.length === backs.length ? "" : `${matching.length} match`}</span>
      </div>
      <div class="looks backs">${page.map((b) => this.renderLook(b.id, current, b.name,
        html`<span class="back"><img src=${cardBackUrl(b.id)} alt="" loading="lazy" decoding="async"></span>`, `by ${b.creator}`))}</div>
      ${matching.length > page.length ? html`<p class="more"><button class="link-button" @click=${() => { this.shown += BACKS_PER_PAGE; }}>
        Show ${Math.min(BACKS_PER_PAGE, matching.length - page.length)} more of ${matching.length - page.length}</button></p>` : nothing}
      <p class="bar">From MPC Autofill, each credited to the person who made it.</p>
    `;
  }

  private renderLooks(p: Progress, current: string | null): TemplateResult {
    switch (this.cosmetic) {
      case "card-back": return this.renderCardBacks(current);
      case "card-frame": return html`<div class="looks frames">${p.card_frames.map((s) => this.renderLook(s.id, current, s.name, cardFramePreview(s.id)))}</div>`;
      case "name-plate": return html`<div class="looks plates">${p.name_plates.map((s) => this.renderLook(s.id, current, s.name, namePlatePreview(s.id, s.name)))}</div>`;
      case "battlefield": return html`<div class="looks fields">${p.battlefields.map((b) => this.renderLook(b.id, current, b.card,
        battlefieldPreview(b.id), `by ${b.artist}, ${b.set_name}`))}</div>`;
    }
  }

  render(): TemplateResult {
    const p = this.progress;
    if (!p) return html`<p class="locked">Reading your level…</p>`;
    const needed = levelUnlocking(p, this.cosmetic);
    if (p.level < needed) {
      return html`<div class="locked">${levelShield(needed, true)}<span><b>Unlocks at level ${needed}</b>
        ${xp(xpToReach(p, needed))} XP to go. You're level ${p.level}.</span></div>`;
    }
    const current = chosenOf(p, this.cosmetic);
    return html`
      ${current ? html`<p class="bar">${this.cosmetic === "card-back" ? "Your card back is marked." : html`<span>${chosenName(p, this.cosmetic, current)} is yours.</span>`}
        <button class="link-button" ?disabled=${this.saving} @click=${() => void this.saveChoice(null)}>Clear it</button></p>` : nothing}
      ${this.renderLooks(p, current)}
      ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
    `;
  }
}

const BACKS_PER_PAGE = 60;

declare global {
  interface HTMLElementTagNameMap { "cosmetic-picker": CosmeticPicker }
}
